class FaceMetricsHandler {
  constructor(dataChannel, viewerId) {
    this.dataChannel = dataChannel;
    this.viewerId = viewerId;

    // State tracking
    this.eyesClosedStartTime = 0;
    this.areEyesClosed = false;
    this.lookingAwayStartTime = 0;
    this.isLookingAway = false;

    // Warning tracking
    this.warningCount = 0;
    this.MAX_WARNINGS = 3;
    this.isWarningDialogOpen = false;
    this.currentWarningMessage = "";

    // Jaw movement tracking
    this.jawMovements = [];
    this.lastJawState = false;
    this.jawMovementWindow = 3000;

    // Initialize localStorage structure
    this.ensureStorageStructure();

    // Cooldowns
    this.lastAlertTime = {
      eyes_closed: 0,
      looking_away: 0,
      speaking: 0,
    };
    this.ALERT_COOLDOWN = 2000;

    // Cache checking setup
    this.lastCacheCheckTime = Date.now();
    this.CACHE_CHECK_INTERVAL = 20000;
    this.cacheCheckTimeout = null;

    // Start the first check cycle
    this.scheduleNextCacheCheck();

    // Thresholds
    this.THRESHOLDS = {
      EYES_CLOSED: {
        VALUE: 0.5,
        DURATION: 3000,
      },
      LOOK_AWAY: {
        VALUE: 0.5,
        DURATION: 3000,
      },
      SPEAKING: {
        VALUE: 0.4,
        MIN_MOVEMENTS: 3,
      },
    };

    console.log(
      "[FaceMetricsHandler] Initialized with data channel state:",
      dataChannel?.readyState
    );
  }

  // Schedule next cache check
  scheduleNextCacheCheck() {
    if (this.cacheCheckTimeout) {
      clearTimeout(this.cacheCheckTimeout);
    }
    this.cacheCheckTimeout = setTimeout(() => {
      this.checkAndClearCache();
      this.scheduleNextCacheCheck();
    }, this.CACHE_CHECK_INTERVAL);
  }

  // Clean up resources
  cleanup() {
    if (this.cacheCheckTimeout) {
      clearTimeout(this.cacheCheckTimeout);
      this.cacheCheckTimeout = null;
    }
  }

  // Helper to ensure localStorage structure exists
  ensureStorageStructure() {
    try {
      const cache = JSON.parse(localStorage.getItem("suspiciousActions"));
      // Check if structure is valid
      if (
        !cache ||
        !cache.eyes_closed ||
        !cache.looking_away ||
        !cache.speaking ||
        !Array.isArray(cache.eyes_closed) ||
        !Array.isArray(cache.looking_away) ||
        !Array.isArray(cache.speaking)
      ) {
        console.log("[FaceMetricsHandler] Initializing localStorage structure");
        localStorage.setItem(
          "suspiciousActions",
          JSON.stringify({
            eyes_closed: [],
            looking_away: [],
            speaking: [],
          })
        );
      }
    } catch (error) {
      console.error(
        "[FaceMetricsHandler] Error reading localStorage, reinitializing:",
        error
      );
      localStorage.setItem(
        "suspiciousActions",
        JSON.stringify({
          eyes_closed: [],
          looking_away: [],
          speaking: [],
        })
      );
    }
  }

  // Check our cache and send alerts if needed
  checkAndClearCache() {
    console.log("[FaceMetricsHandler] Checking suspicious actions cache...");

    // Ensure structure exists
    this.ensureStorageStructure();

    try {
      const cache = JSON.parse(localStorage.getItem("suspiciousActions"));
      let shouldShowWarning = false;
      let warningType = "";
      let warningTimestamps = [];

      // First check if any action type has 3 or more occurrences
      Object.entries(cache).forEach(([type, timestamps]) => {
        if (timestamps.length >= 3) {
          shouldShowWarning = true;
          warningType = type;
          warningTimestamps = timestamps;
        }
      });

      // If we found a violation, show the warning
      if (shouldShowWarning) {
        // Increment warning count
        this.warningCount++;

        // Format warning message
        const formattedType = warningType.replace("_", " ");
        this.currentWarningMessage = `Too many ${formattedType} detected. ${
          this.MAX_WARNINGS - this.warningCount
        } warnings remaining before exam termination.`;

        // Show warning dialog
        this.isWarningDialogOpen = true;
        console.log("[FaceMetricsHandler] Opening warning dialog", {
          message: this.currentWarningMessage,
          count: this.warningCount,
          maxWarnings: this.MAX_WARNINGS,
        });

        // Send alert through data channel
        this.sendAlert(`multiple_${warningType}`, {
          message: `Student has ${formattedType} ${warningTimestamps.length} times in last 10 seconds`,
          count: warningTimestamps.length,
          warningCount: this.warningCount,
        });

        // Track in S3 video system
        if (typeof window.trackViolation === "function") {
          window.trackViolation("face_metrics", {
            type: warningType,
            details: {
              message: this.currentWarningMessage,
              count: warningTimestamps.length,
              warningCount: this.warningCount,
              maxWarnings: this.MAX_WARNINGS,
              timestamp: new Date().toISOString(),
            },
          });
        }
      }

      // Clear all caches after checking
      localStorage.setItem(
        "suspiciousActions",
        JSON.stringify({
          eyes_closed: [],
          looking_away: [],
          speaking: [],
        })
      );
    } catch (error) {
      console.error("[FaceMetricsHandler] Error in checkAndClearCache:", error);
      this.ensureStorageStructure();
    }
  }

  // Add a timestamp to our cache
  trackSuspiciousAction(type) {
    const now = Date.now();

    // Check cooldown
    if (now - this.lastAlertTime[type] < this.ALERT_COOLDOWN) {
      return; // Still in cooldown
    }
    this.lastAlertTime[type] = now;

    try {
      // Ensure structure exists
      this.ensureStorageStructure();

      // Get current cache
      const cache = JSON.parse(localStorage.getItem("suspiciousActions"));

      // Add new timestamp
      cache[type].push(now);

      // Save back to localStorage
      localStorage.setItem("suspiciousActions", JSON.stringify(cache));
    } catch (error) {
      console.error(
        "[FaceMetricsHandler] Error in trackSuspiciousAction:",
        error
      );
      this.ensureStorageStructure();
    }
  }

  // Send alert through data channel
  sendAlert(type, details) {
    // For behavior alerts (speaking, eyes_closed, looking_away)
    const alert = {
      type: "behavior_alert", // This matches proctor's switch case
      alertType: type, // Will be like "multiple_speaking", "multiple_eyes_closed", etc.
      viewerId: this.viewerId,
      timestamp: new Date().toISOString(),
      details: details, // Contains message, count, warningCount
    };

    try {
      console.log(
        "🚨 [FaceMetricsHandler] SENDING TO PROCTOR - BEHAVIOR ALERT:",
        JSON.stringify(alert, null, 2)
      );
      this.dataChannel.send(JSON.stringify(alert));
      console.log("✅ [FaceMetricsHandler] Behavior alert sent successfully");
    } catch (error) {
      console.error(
        "❌ [FaceMetricsHandler] Error sending behavior alert:",
        error
      );
    }
  }

  // Send object alert through data channel (for multiple faces)
  sendObjectAlert(type, details) {
    const alert = {
      type: "object_alert", // This matches proctor's switch case
      alertType: type, // Will be "multiple_faces"
      viewerId: this.viewerId,
      timestamp: new Date().toISOString(),
      details: details, // Should contain message, faceCount
    };

    try {
      console.log(
        "🚨 [FaceMetricsHandler] SENDING TO PROCTOR - OBJECT ALERT:",
        JSON.stringify(alert, null, 2)
      );
      this.dataChannel.send(JSON.stringify(alert));
      console.log("✅ [FaceMetricsHandler] Object alert sent successfully");
    } catch (error) {
      console.error(
        "❌ [FaceMetricsHandler] Error sending object alert:",
        error
      );
    }
  }

  // Process blendshapes data
  processBlendshapes(shapes) {
    if (!shapes) return;
    const now = Date.now();

    // Process eye looking values
    const eyeValues = {
      inL: shapes.eyeLookInLeft || 0,
      inR: shapes.eyeLookInRight || 0,
      outL: shapes.eyeLookOutLeft || 0,
      outR: shapes.eyeLookOutRight || 0,
    };

    // Start looking away if any value > 0.5, stop only if ALL values < 0.3
    const startLookingAway = Object.values(eyeValues).some(
      (val) => val > this.THRESHOLDS.LOOK_AWAY.VALUE
    );
    const stopLookingAway = Object.values(eyeValues).every((val) => val < 0.3);

    // Handle looking away state
    if (startLookingAway && !this.isLookingAway) {
      this.isLookingAway = true;
      this.lookingAwayStartTime = now;
      console.log("⚠️ Started looking away tracking");
    } else if (stopLookingAway && this.isLookingAway) {
      console.log(
        "✅ Stopped looking away, duration:",
        ((now - this.lookingAwayStartTime) / 1000).toFixed(1),
        "seconds"
      );
      this.isLookingAway = false;
      this.lookingAwayStartTime = 0;
    }

    // Send alert if looking away for too long
    if (this.isLookingAway && this.lookingAwayStartTime > 0) {
      const lookingAwayDuration = now - this.lookingAwayStartTime;
      const cooldownTime = now - (this.lastAlertTime.looking_away || 0);

      if (
        lookingAwayDuration >= this.THRESHOLDS.LOOK_AWAY.DURATION &&
        cooldownTime >= this.ALERT_COOLDOWN
      ) {
        console.log("🚨 Looking away detected!");
        console.log(
          `Looking away duration: ${(lookingAwayDuration / 1000).toFixed(1)}s`,
          `Values: In[L:${eyeValues.inL.toFixed(2)} R:${eyeValues.inR.toFixed(
            2
          )}]`,
          `Out[L:${eyeValues.outL.toFixed(2)} R:${eyeValues.outR.toFixed(2)}]`
        );

        // Track in both systems
        this.trackSuspiciousAction("looking_away");
      }
    }

    // Process speaking - track jaw movements
    const isJawOpen = shapes.jawOpen >= this.THRESHOLDS.SPEAKING.VALUE;

    // Detect jaw movement (transition between open and closed)
    if (isJawOpen !== this.lastJawState) {
      this.jawMovements.push(now);
      this.lastJawState = isJawOpen;

      // Remove old movements outside the window
      const windowStart = now - this.jawMovementWindow;
      this.jawMovements = this.jawMovements.filter(
        (time) => time >= windowStart
      );

      // Check if we have enough movements in the window
      if (this.jawMovements.length >= this.THRESHOLDS.SPEAKING.MIN_MOVEMENTS) {
        // Track in both systems
        this.trackSuspiciousAction("speaking");
        // Reset after tracking
        this.jawMovements = [];
      }
    }

    // Process eyes closed
    const eyesClosed =
      shapes.eyeBlinkLeft >= this.THRESHOLDS.EYES_CLOSED.VALUE &&
      shapes.eyeBlinkRight >= this.THRESHOLDS.EYES_CLOSED.VALUE;

    if (eyesClosed && !this.areEyesClosed) {
      // Eyes just closed
      this.areEyesClosed = true;
      this.eyesClosedStartTime = now;
    } else if (!eyesClosed && this.areEyesClosed) {
      // Eyes just opened
      this.areEyesClosed = false;
      this.eyesClosedStartTime = 0;
    }

    if (
      this.areEyesClosed &&
      now - this.eyesClosedStartTime >= this.THRESHOLDS.EYES_CLOSED.DURATION
    ) {
      // Track in both systems
      this.trackSuspiciousAction("eyes_closed");
      // Reset the start time but keep areEyesClosed true
      this.eyesClosedStartTime = now;
    }
  }
}

export default FaceMetricsHandler;
